import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as DashboardSvg } from "../../assets/images/icons/dashboard.svg";
import { ReactComponent as OrderSvg } from "../../assets/images/icons/orders.svg";
import { ReactComponent as ProductSvg } from "../../assets/images/icons/product.svg";
import { authService } from "../../service/AuthService";
import colors from "../../theme/color/ColorTheme";
import fontSize from "../../theme/fonts/FontTheme";
import FilledButton from "../button/FilledButton";
import LogoCircular from "../logo/LogoCircle";
import LogoFull from "../logo/LogoFull";

const CollapseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z"
      fill="currentColor"
    />
  </svg>
);

const Wrapper = styled.div`
  background-color: ${colors.backgroundPrimary};
  width: ${(props) => (props.collapsed ? "80px" : "260px")};
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${colors.dash};
  /* box-shadow: 0 0 20px rgba(0, 0, 0, 0.1); */
  transition: width 0.3s ease;
  z-index: 1000;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const LogoContainer = styled.div`
  padding: 0 24px 20px;
  border-bottom: 1px solid ${colors.dash};
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  justify-content: ${(props) => (props.collapsed ? "center" : "flex-start")};
  .mobile.logo {
    display: ${(props) => (props.collapsed ? "initial" : "none")};
  }
  .desktop.logo {
    display: ${(props) => (props.collapsed ? "none" : "initial")};
  }
`;

const MenuItems = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
`;

const MenuItem = styled.li`
  padding: 8px 10px;
  margin: 4px 14px;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: ${fontSize.s};
  color: ${(props) => (props.active ? "white" : "#1E1E1E")};
  background: ${(props) => (props.active ? "black" : "transparent")};
  border-radius: 8px;
  transition: all 0.2s ease;
  &:hover {
    color: ${(props) => (props.active ? "white" : colors.textSecondary)};
  }

  svg {
    margin-right: ${(props) => (props.collapsed ? "0px" : "12px")};
    color: ${(props) => (props.active ? "white" : "inherit")};
  }

  span {
    display: ${(props) => (props.collapsed ? "none" : "inline")};
    white-space: nowrap;
    padding: 0px;
    margin: 0px;
  }
`;

const Spacer = styled.div`
  height: ${(props) => props.size || "20px"};
`;

const ToggleButton = styled.button`
  background: ${colors.backgroundLight};
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  bottom: 20px;
  right: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary};
    color: white;
  }
`;

const MenuLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.textTertiary};
  padding: 0 24px;
  margin: 16px 0 12px;
  display: ${(props) => (props.collapsed ? "none" : "block")};
`;

function AdminSidebar({ collapsed }) {
  const [activeItem, setActiveItem] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <DashboardSvg />,
      link: "/admin/dashboard",
    },
    {
      id: "products",
      label: "Products",
      icon: <ProductSvg />,
      link: "/admin/products",
    },
    {
      id: "orders",
      label: "Orders",
      icon: <OrderSvg />,
      link: "/admin/orders",
    },
    // {
    //   id: "categories",
    //   label: "Categories",
    //   icon: <OrderSvg />,
    //   link: "/admin/categories/details",
    // },
  ];
  useEffect(() => {
    const current = menuItems.find((item) =>
      location.pathname.startsWith(item.link)
    );
    if (current) {
      setActiveItem(current.label);
    }
  }, [location.pathname]);
  return (
    <Wrapper collapsed={collapsed}>
      <LogoContainer
        collapsed={collapsed}
        onClick={() => {
          navigate("/");
        }}
      >
        <div className="desktop logo">
          <LogoFull />
        </div>
        <div className="mobile logo">
          <LogoCircular height={"50px"} />
        </div>
      </LogoContainer>

      <MenuItems>
        <MenuLabel collapsed={collapsed}>Main Menu</MenuLabel>
        {menuItems.map((item) => (
          <MenuItem
            key={item.id}
            active={activeItem === item.label}
            collapsed={collapsed}
            onClick={() => {
              setActiveItem(item.label);
              navigate(item.link);
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </MenuItem>
        ))}

        <Spacer size="40px" />

        <MenuLabel collapsed={collapsed}>Analytics</MenuLabel>
        <MenuItem collapsed={collapsed}>
          <DashboardSvg />
          <span>Reports</span>
        </MenuItem>
        <MenuItem collapsed={collapsed}>
          <ProductSvg />
          <span>Statistics</span>
        </MenuItem>
      </MenuItems>
      <MenuItem>
        <FilledButton
          onClick={() => {
            authService.logout(
              (message) => {
                alert(message);
                navigate("/");
              },
              () => {
                alert("Error logging out");
              }
            );
          }}
          text={"Logout"}
        ></FilledButton>
      </MenuItem>

      {/* <ToggleButton onClick={() => setCollapsed(!collapsed)}>
        <CollapseIcon />
      </ToggleButton> */}
    </Wrapper>
  );
}

export default AdminSidebar;
